import { isNonEmpty } from '@sgme/fp';
import { getTradeWayLabel } from 'components/secondaryMarket/shared/status/tradeWay/useTradeWayLabel';
import { parseISO } from 'date-fns';
import type { IntlKey } from 'locales';
import { useMemo } from 'react';
import { PrimitiveType, useIntl } from 'react-intl';
import type { TradeWay } from 'state/secondaryMarket/secondaryMarket.model';

export interface OrderPlacedFormattingProps {
  execTime: string;
  traderLabel?: string;
  productIsin?: string;
  productRic?: string;
  tradeWay: TradeWay;
  quantity?: number;
  fileCode?: string;
  formattedCashAmountLabel: IntlKey;
  formattedCashAmountValues?: Record<string, PrimitiveType>;
}
export const useFormattedOrderPlaced = ({
  tradeWay,
  execTime,
  productIsin,
  traderLabel,
  quantity,
  productRic,
  fileCode,
  formattedCashAmountLabel,
  formattedCashAmountValues,
}: OrderPlacedFormattingProps) => {
  const { formatMessage, formatTime, formatNumber } = useIntl();
  const formattedQuantity = !quantity || isNaN(quantity) ? '-' : formatNumber(quantity);
  const formattedCashAmount = formatMessage({ id: formattedCashAmountLabel }, formattedCashAmountValues);
  const formattedExecTime = useMemo(() => {
    if (execTime === undefined) {
      return undefined;
    }
    return formatTime(parseISO(execTime));
  }, [execTime, formatTime]);
  const productCode = useMemo(
    () => [productIsin, productRic, fileCode].find(isNonEmpty),
    [productIsin, productRic, fileCode],
  );
  const tradeWayLabel = useMemo(
    () =>
      formatMessage({
        id: getTradeWayLabel(tradeWay),
      }),
    [formatMessage, tradeWay],
  );
  const summary = useMemo(
    () =>
      formatMessage(
        { id: 'secondaryMarket.sidePanel.label.orderPlacedSummary' },
        {
          tradeWay: tradeWayLabel,
          traderLabel: traderLabel ?? '_',
          productCode: productCode ?? '_',
          qty: formattedQuantity,
          cashAmount: formattedCashAmount,
        },
      ),
    [formatMessage, formattedCashAmount, formattedQuantity, productCode, tradeWayLabel, traderLabel],
  );
  const hint = useMemo(() => {
    if (formattedExecTime) {
      return formatMessage(
        { id: 'secondaryMarket.sidePanel.label.orderPlacedHintAt' },
        { when: formattedExecTime, productCode: productCode },
      );
    } else {
      return null;
    }
  }, [formatMessage, formattedExecTime, productCode]);
  return { summary, hint, formatMessage };
};
